import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { Query, ValidateDescriptionInput } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const VALIDATE_DESCRIPTION_QUERY = gql`
  query validateDescription($tenantID: ID!, $description: String!) {
    validateDescription(
      request: { tenantID: $tenantID, description: $description }
    ) {
      validForQuestions {
        question
        isValid
      }
      message
    }
  }
`;

interface ValidateIntegrationDescriptionOptions {
  onCompleted?: () => void;
}

export const useValidateIntegrationDescription = ({
  onCompleted,
}: ValidateIntegrationDescriptionOptions = {}) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const [validateDescriptionQuery, { data, loading: isLoading }] = useLazyQuery<
    Query,
    ValidateDescriptionInput
  >(VALIDATE_DESCRIPTION_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to validate description.',
        props: {},
      });
    },
    onCompleted,
  });

  const validateDescription = useCallback(
    async (description: string) => {
      return validateDescriptionQuery({
        variables: { tenantID, description },
      });
    },
    [tenantID, validateDescriptionQuery],
  );

  return [
    validateDescription,
    {
      validForQuestions: data?.validateDescription?.validForQuestions ?? [],
      message: data?.validateDescription?.message ?? '',
      isLoading,
    },
  ] as const;
};
